import { CodeCommand } from "commands/CodeCommand";
import { EmailCommand } from "commands/EmailCommand";
import { ChangePasswordCommand } from "commands/account/ChangePasswordCommand";
import { EmailLoginCommand } from "commands/account/EmailLoginCommand";
import { RecoverPasswordCommand } from "commands/account/RecoverPasswordCommand";
import { RegisterTeacherCommand } from "commands/account/RegisterTeacherCommand";
import { RegisterStudentCommand } from "commands/account/RegisterStudentCommand";
import { RequestEmailChangeCommand } from "commands/account/RequestEmailChangeCommand";
import { ConfirmEmailChangeCommand } from "commands/account/ConfirmEmailChangeCommand";
import { sessionId } from "contexts/GlobalContext";
import { GlobalDataResult } from "results/account/GlobalDataResult";
import { useApiService } from "services/ApiService";
import { TeacherLoginResult } from "results/account/TeacherLoginResult";
import { CodeLoginResult } from "results/account/CodeLoginResult";
import { RecoverPasswordResult } from "results/account/RecoverPasswordResult";
import { ConfirmEmailChangeResult } from "results/account/ConfirmEmailChangeResult";
import { RegisterStudentResult } from "results/account/RegisterStudentResult";
import { SuccessResult } from "results/SuccessResult";
import { VerifyRegistrationCodeCommand } from "commands/account/VerifyRegistrationCodeCommand";
import { VerifyReferrerCodeResult } from "results/account/VerifyReferrerCodeResult";
import { VerifySchoolCodeResult } from "results/account/VerifySchoolCodeResult";
import { VerifyEmailResult } from "results/account/VerifyEmailResult";
import { VerifyClassCodeResult } from "results/account/VerifyClassCodeResult";
import { ResolveCodeResult } from "results/codes/ResolveCodeResult";
import { GoogleLoginCommand } from "commands/account/GoogleLoginCommand";
import { GetTeacherRegisterResult } from "results/account/GetTeacherRegisterResult";
import { TeacherLoginMethodResult } from "results/account/TeacherLoginMethodResult";
import { NswCompleteCommand } from "commands/account/NswCompleteCommand";
import { NswCompleteResult } from "results/account/NswCompleteResult";

export const AccountApiService = () => {
    const { get, post } = useApiService({ controller: "/account" });

    const buildNswChallengeUrl = (props?: { returnUrl?: string; description?: string }) => {
        const search = new URLSearchParams();

        search.set("sessionId", sessionId);

        if (props?.returnUrl) {
            search.set("returnUrl", props.returnUrl);
        }

        if (props?.description) {
            search.set("description", props.description);
        }

        return `/api/account/nsw/challenge?${search.toString()}`;
    };

    return {
        loginEmail: (command: EmailLoginCommand) => post<TeacherLoginResult>("/login-email", { ...command, sessionId }),
        loginMethod: (email: string) =>
            get<TeacherLoginMethodResult>(`/login-method?email=${encodeURIComponent(email)}`),
        loginGoogle: (command: GoogleLoginCommand) =>
            post<TeacherLoginResult>("/login-google", { ...command, sessionId }),
        loginCode: (command: CodeCommand) => post<CodeLoginResult>("/login-code", { ...command, sessionId }),
        registerTeacher: (command: RegisterTeacherCommand) =>
            post<TeacherLoginResult>("/register-teacher", { ...command, sessionId }),
        registerEmail: (command: EmailCommand) => post("/register-email", command),
        registerGoogle: (command: CodeCommand) => post<VerifyEmailResult>("/register-google", command),
        registerStudent: (command: RegisterStudentCommand) =>
            post<RegisterStudentResult>("/register-student", { ...command, sessionId }),
        changePassword: (command: ChangePasswordCommand) => post("/change-password", command),
        forgotPassword: (command: EmailCommand) => post("/forgot-password", command),
        getRecoverPassword: (hash: string) => get<SuccessResult>(`/recover-password/${hash}`),
        recoverPassword: (command: RecoverPasswordCommand) => post<RecoverPasswordResult>("/recover-password", command),
        getGlobalUserData: () => get<GlobalDataResult>("/global-data"),
        verifyRegistrationCode: (command: VerifyRegistrationCodeCommand) =>
            post<VerifyEmailResult>("/verify-registration-code", command),
        verifyClassCode: (command: CodeCommand) => post<VerifyClassCodeResult>("/verify-class-code", command),
        verifyReferrerCode: (command: CodeCommand) => post<VerifyReferrerCodeResult>("/verify-referrer-code", command),
        verifySchoolCode: (command: CodeCommand) => post<VerifySchoolCodeResult>("/verify-school-code", command),
        getTeacherRegister: () => get<GetTeacherRegisterResult>("/teacher-register"),
        requestEmailChange: (command: RequestEmailChangeCommand) => post("/request-email-change", command),
        confirmEmailChange: (command: ConfirmEmailChangeCommand) =>
            post<ConfirmEmailChangeResult>("/confirm-email-change", command),
        nswComplete: (command: NswCompleteCommand) => post<NswCompleteResult>("/nsw/complete", command),
        getNswChallengeUrl: (props?: { returnUrl?: string; description?: string }) => buildNswChallengeUrl(props),
        resolveCode: (code: string) => get<ResolveCodeResult>(`/resolve-code/${code}`),
    };
};
